// ink-component
import * as React from 'react';
import styled from 'styled-components';

import { newTableTokens } from '../../../../tokens/components/NewTable/tokens';
import { NewTableVirtualContext, NewTableVirtualizedElement } from '../NewTableVirtualWrapper/NewTableVirtualWrapper';
import { TwiddleOpenContext, TwiddleOpenContextType } from '../NewTableTwiddle/contexts';

import CoreRow from '../../_common/CoreRow';

export interface NewTableBodyProps extends React.TableHTMLAttributes<HTMLTableSectionElement> {
  height?: number;

  /** @ignore */
  onTwiddleContextMount?: React.Dispatch<React.SetStateAction<TwiddleOpenContextType>>;
}

/* @internal */
export const StyledBody = styled.tbody<NewTableBodyProps & NewTableVirtualizedElement>`
  ${({ virtual }) =>
    virtual &&
    `
      display: grid;
      position: relative;
  `}

  // Adds a top border to first row if there isn't a header defined
  &:first-child ${CoreRow}:first-of-type {
    border-top: 1px solid ${newTableTokens.CellBorderColorBaseDefault};
  }
`;

/**
 * @group NewTable
 * @purpose Wraps around internal rows
 * @categories table
 * @HTMLType HTMLTableBody
 */
function NewTableBody({ style, className, height, children, onTwiddleContextMount, ...otherProps }: NewTableBodyProps) {
  const virtual = React.useContext(NewTableVirtualContext);
  const twiddle = React.useContext(TwiddleOpenContext);

  const type = virtual?.type;
  const context = React.useMemo(() => ({ section: 'body' as const, type: type! }), [type]);

  React.useEffect(() => {
    if (onTwiddleContextMount) {
      onTwiddleContextMount(twiddle);
    }
  }, [twiddle, onTwiddleContextMount]);

  if (!virtual) {
    return <StyledBody {...otherProps}>{children}</StyledBody>;
  }

  /*
    `virtual` is true: below is the virtualized tbody.
  */
  if (height === undefined) {
    throw new Error('NewTable.Body: Virtualization requires spreading ...virtualizer.getBodyProps() to NewTable.Body.');
  }

  return (
    <StyledBody virtual style={{ height: virtual ? `${height}px` : undefined }} {...otherProps}>
      {/*
          Table head and table body rows have different styling requirements. In a virtualized
          environment `virtual` will be `true`, so we reinstantiate the context here to ensure
          that children rows of NewTable.Body are styled correctly.
        */}
      <NewTableVirtualContext.Provider value={context}>{children}</NewTableVirtualContext.Provider>
    </StyledBody>
  );
}

NewTableBody.displayName = 'NewTable.Body';

export default NewTableBody;
